if (!customElements.get("service-intake")) {
  customElements.define(
    "service-intake",
    class ServiceIntake extends HTMLElement {
      constructor() {
        super()
        this.history = []
        this.currentStepId = null
        this.selections = {}
        this.config = null
        this.showLanding = true
      }

      connectedCallback() {
        const scriptTag = this.querySelector('script[type="application/json"]')
        if (!scriptTag) {
          console.error("ServiceIntake: Configuration JSON script element not found.")
          return
        }

        try {
          this.config = JSON.parse(scriptTag.textContent)
        } catch (e) {
          console.error("ServiceIntake: Failed to parse configuration JSON.", e)
          return
        }

        this.stepContainer = this.querySelector("#IntakeStepContainer")
        this.prevBtn = this.querySelector("#IntakePrevBtn")
        this.nextBtn = this.querySelector("#IntakeNextBtn")
        this.navsContainer = this.querySelector("#IntakeNavs")
        this.breadcrumbsContainer = this.querySelector("#IntakeBreadcrumbs")
        if (!this.stepContainer || !this.prevBtn || !this.breadcrumbsContainer) {
          console.error("ServiceIntake: Missing required child elements.")
          return
        }

        // Bind events
        this._prevStepHandler = () => this.prevStep()
        this.prevBtn.addEventListener("click", this._prevStepHandler)
        if (this.nextBtn) {
          this._nextStepHandler = () => this.nextStep()
          this.nextBtn.addEventListener("click", this._nextStepHandler)
        }
        // Initialize state
        this.history = []
        this.currentStepId = this.config.start_step_id || "start"
        this.selections = {}
        this.showLanding = true

        // Start fetching dynamic brands
        this.dynamicBrandsPromise = this.fetchDynamicBrands()

        // Warm Calendly JS/CSS and CDN connection on idle, before the user reaches the booking step
        const calendlyModal = document.getElementById("calendly-modal")
        if (calendlyModal && calendlyModal.load) {
          const firstCalendlyUrl = Object.values(this.config.steps).find(
            s => s.type === "outcome" && s.is_calendly && !s.embed_calendly && s.button_url
          )?.button_url
          if (firstCalendlyUrl) {
            window.addEventListener("load", () => calendlyModal.load(firstCalendlyUrl), {once: true})
          }
        }

        this.renderLandingStep()
      }
      disconnectedCallback() {
        if (this.abortController) {
          this.abortController.abort()
        }
        clearTimeout(this.autoProgressTimeout)
        if (this.prevBtn && this._prevStepHandler) {
          this.prevBtn.removeEventListener("click", this._prevStepHandler)
        }
        if (this.nextBtn && this._nextStepHandler) {
          this.nextBtn.removeEventListener("click", this._nextStepHandler)
        }
      }

      async fetchDynamicBrands() {
        this.abortController = new AbortController()
        const includeShopifyBrands = this.config.include_shopify_brands !== false
        const collectionHandle = this.config.collection || "bikes-and-scooters"
        const extraBrands = Array.isArray(this.config.hard_coded_brands)
          ? this.config.hard_coded_brands.map(brand => (brand || "").trim()).filter(Boolean)
          : []
        let bikeVendors = []
        if (includeShopifyBrands) {
          try {
            const res = await fetch(`/collections/${collectionHandle}/products.json?limit=250`, {
              signal: this.abortController.signal
            })
            if (res.ok) {
              const data = await res.json()
              if (data && Array.isArray(data.products)) {
                bikeVendors = data.products
                  .filter(p => {
                    const t = (p.product_type || "").toLowerCase()
                    return t === "electric bikes" || t === "electric bike"
                  })
                  .map(p => p.vendor)
                  .filter(Boolean)
              }
            }
          } catch (err) {
            if (err.name !== "AbortError") {
              console.error("Error fetching collection products:", err)
            }
          }
        }
        const seen = new Set()
        const uniqueBrands = []
        const allBrands = [...extraBrands, ...bikeVendors]
        for (const brand of allBrands) {
          if (!brand) continue
          const trimmed = brand.trim()
          const lower = trimmed.toLowerCase()
          if (!seen.has(lower)) {
            seen.add(lower)
            uniqueBrands.push(trimmed)
          }
        }
        this.dynamicBrands = uniqueBrands.sort((a, b) => a.localeCompare(b, undefined, {sensitivity: "base"}))
      }

      renderLandingStep() {
        clearTimeout(this.autoProgressTimeout)
        this.showLanding = true
        this.stepContainer.innerHTML = ""

        if (this.breadcrumbsContainer) this.breadcrumbsContainer.classList.add("hidden")
        if (this.navsContainer) this.navsContainer.classList.add("hidden")

        const landingEl = document.createElement("div")
        landingEl.className = "intake-step intake-step--landing active"

        const icon = document.createElement("div")
        icon.className = "intake-landing-icon"
        icon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10"/>
          <path d="M12 6v6l4 2"/>
        </svg>`
        landingEl.appendChild(icon)

        const badge = document.createElement("span")
        badge.className = "intake-landing-badge"
        badge.textContent = "Bike Service"
        landingEl.appendChild(badge)

        const heading = document.createElement("h1")
        heading.className = "intake-landing-title"
        heading.textContent = "Service intake form"
        landingEl.appendChild(heading)

        const description = document.createElement("p")
        description.className = "text-body intake-landing-description"
        description.textContent =
          "Let\u2019s make sure Casa Bikes can provide the service you need with a few quick questions."
        landingEl.appendChild(description)

        const button = document.createElement("button")
        button.type = "button"
        button.className = "button button--primary intake-landing-button"
        button.innerHTML = `
          <span>Get started</span>
          <span class="button-icon-right" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="icon-arrow"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
          </span>
        `
        button.addEventListener("click", () => {
          this.showLanding = false
          this.renderStep(this.currentStepId)
        })
        landingEl.appendChild(button)

        this.stepContainer.appendChild(landingEl)
      }

      renderStep(stepId) {
        this.showLanding = false

        const step = this.config.steps[stepId]
        if (!step) {
          console.error(`ServiceIntake: Step "${stepId}" not found in config.`)
          this.stepContainer.innerHTML = `
            <div class="intake-result intake-result--reject">
              <h2>Error</h2>
              <p>Step "${stepId}" is missing or misconfigured in the theme blocks.</p>
            </div>
          `
          if (this.navsContainer) this.navsContainer.classList.add("hidden")
          return
        }

        this.stepContainer.innerHTML = ""
        this.updateBreadcrumbs()
        this.pruneFutureSelections()

        if (step.type === "question") {
          this.renderQuestionStep(step)
        } else if (step.type === "brand_check") {
          this.renderBrandCheckStep(step)
        } else if (step.type === "outcome") {
          this.renderOutcomeStep(step)
        }

        this.scrollToWizardTop()
        this.validateStep()
      }

      scrollToWizardTop() {
        if (window.innerWidth >= 750) return
        window.scrollTo({top: 0, behavior: "instant"})
      }

      renderQuestionStep(step) {
        if (this.navsContainer) this.navsContainer.classList.remove("hidden")

        // Preload exact prefilled Calendly widgets for any options that open a popup.
        // Simulates each selection temporarily to build the URL that modal.open() will use,
        // so the widget Map has a cache hit the moment the user clicks.
        const calendlyModalEl = document.getElementById("calendly-modal")
        if (calendlyModalEl && calendlyModalEl.load) {
          for (const [idx, opt] of (step.options || []).entries()) {
            const target = opt.next && this.config.steps[opt.next]
            if (
              !target ||
              target.type !== "outcome" ||
              !target.is_calendly ||
              target.embed_calendly ||
              !target.button_url
            )
              continue
            const prev = this.selections[step.id]
            this.selections[step.id] = idx
            const url = this.buildPrefilledCalendlyUrl(target.button_url)
            if (prev === undefined) delete this.selections[step.id]
            else this.selections[step.id] = prev
            calendlyModalEl.load(url)
          }
        }

        const stepEl = document.createElement("div")
        stepEl.className = "intake-step active"
        stepEl.setAttribute("data-step-id", step.id)

        const h3 = document.createElement("h3")
        h3.className = "h2"
        h3.textContent = step.title
        stepEl.appendChild(h3)

        if (step.description) {
          const p = document.createElement("p")
          p.className = "text-body"
          p.textContent = step.description
          stepEl.appendChild(p)
        }

        const optionsGrid = document.createElement("div")
        const useCompactRows = Boolean(step.compact_rows)
        const useCompactMobileGrid = !useCompactRows && step.options.length >= 4
        optionsGrid.className = "intake-options-grid"
        if (useCompactRows) {
          optionsGrid.classList.add("intake-options-grid--rows")
        } else if (step.options.length <= 2) {
          optionsGrid.classList.add("grid-half")
        } else if (step.options.length === 3) {
          optionsGrid.classList.add("grid-third")
        } else if (step.options.length >= 4) {
          optionsGrid.classList.add("grid-four")
        }
        if (useCompactMobileGrid) {
          optionsGrid.classList.add("intake-options-grid--compact-mobile")
        }

        for (const [idx, opt] of (step.options || []).entries()) {
          if (!opt.label) continue

          const button = document.createElement("button")
          button.type = "button"
          button.className = "intake-option-card"
          if (useCompactRows) {
            button.classList.add("intake-option-card--row")
          }
          button.setAttribute("data-index", idx)

          if (this.config.show_option_images && !useCompactRows) {
            let imageUrl = opt.image
            if (!imageUrl) {
              const fallbackImages = this.config.fallback_images || {}
              const labelLower = opt.label.toLowerCase()
              if (labelLower.includes("scooter") || labelLower.includes("electrical")) {
                imageUrl = fallbackImages.scooter || ""
              } else if (
                labelLower.includes("mechanical") ||
                labelLower.includes("bicycle") ||
                labelLower.includes("bike")
              ) {
                imageUrl = fallbackImages.bike || ""
              } else if (
                labelLower.includes("motorcycle") ||
                labelLower.includes("moped") ||
                labelLower.includes("moto")
              ) {
                imageUrl = fallbackImages.moto || ""
              } else {
                imageUrl = fallbackImages.fallback || ""
              }
            }

            if (imageUrl) {
              button.classList.add("has-image")
              const img = document.createElement("img")
              img.src = imageUrl
              img.alt = opt.label
              img.className = "intake-option-image"
              img.loading = "lazy"
              button.appendChild(img)
            }
          }

          const span = document.createElement("span")
          span.textContent = opt.label
          button.appendChild(span)

          if (opt.description && opt.description.trim() !== "") {
            const desc = document.createElement("p")
            desc.className = "intake-option-description"
            desc.textContent = opt.description
            button.appendChild(desc)
          }
          if (this.selections[step.id] === idx) {
            button.classList.add("selected")
          }

          button.addEventListener("click", () => {
            for (const b of optionsGrid.querySelectorAll(".intake-option-card")) {
              b.classList.remove("selected")
            }
            button.classList.add("selected")
            this.selections[step.id] = idx
            this.validateStep()

            // Auto-progress to the next step after a short delay
            if (this.autoProgressTimeout) {
              clearTimeout(this.autoProgressTimeout)
            }
            this.autoProgressTimeout = setTimeout(() => {
              if (this.currentStepId !== step.id) return
              const nextStep = opt.next && this.config.steps[opt.next]
              if (
                nextStep &&
                nextStep.type === "outcome" &&
                nextStep.is_calendly &&
                !nextStep.embed_calendly &&
                nextStep.button_url
              ) {
                const url = this.buildPrefilledCalendlyUrl(nextStep.button_url)
                const modal = document.getElementById("calendly-modal")
                if (modal && modal.open) modal.open(url, button)
              } else {
                this.nextStep()
              }
            }, 300)
          })

          optionsGrid.appendChild(button)
        }

        stepEl.appendChild(optionsGrid)
        this.stepContainer.appendChild(stepEl)
      }

      async renderBrandCheckStep(step) {
        if (this.navsContainer) this.navsContainer.classList.remove("hidden")

        const stepEl = document.createElement("div")
        stepEl.className = "intake-step active"
        stepEl.setAttribute("data-step-id", step.id)

        const h3 = document.createElement("h3")
        h3.className = "h2"
        h3.textContent = step.title
        stepEl.appendChild(h3)

        if (step.description) {
          const p = document.createElement("p")
          p.className = "text-body"
          p.textContent = step.description
          stepEl.appendChild(p)
        }

        if (this.dynamicBrandsPromise) {
          await this.dynamicBrandsPromise
        }
        if (!this.selections[step.id] || typeof this.selections[step.id] !== "object") {
          this.selections[step.id] = {
            brand: "",
            motor: "",
            customBrand: ""
          }
        }
        const savedVal = this.selections[step.id]

        // Brand dropdown container
        const brandFieldEl = document.createElement("div")
        brandFieldEl.className = "intake-brand-select-wrapper"

        const brandSelect = document.createElement("select")
        brandSelect.className = "intake-brand-select"

        const brandPlaceholder = document.createElement("option")
        brandPlaceholder.value = ""
        brandPlaceholder.disabled = false
        brandPlaceholder.selected = !savedVal.brand
        brandPlaceholder.textContent = "Choose brand..."
        brandSelect.appendChild(brandPlaceholder)

        const rawBrands =
          this.dynamicBrands && this.dynamicBrands.length > 0
            ? this.dynamicBrands
            : (step.supported_brands || []).map(b => b.trim()).filter(Boolean)
        const normalizedBrands = [...new Set(rawBrands)].sort((a, b) =>
          a.localeCompare(b, undefined, {sensitivity: "base"})
        )

        for (const brand of normalizedBrands) {
          if (!brand) continue
          const opt = document.createElement("option")
          opt.value = brand
          opt.textContent = brand
          if (savedVal.brand && savedVal.brand.toLowerCase() === brand.toLowerCase()) {
            opt.selected = true
          }
          brandSelect.appendChild(opt)
        }

        const brandOtherOpt = document.createElement("option")
        brandOtherOpt.value = "other"
        brandOtherOpt.textContent = "Other"
        if (savedVal.brand === "other") {
          brandOtherOpt.selected = true
        }
        brandSelect.appendChild(brandOtherOpt)

        const brandChevron = document.createElement("span")
        brandChevron.className = "intake-brand-select-chevron"
        brandChevron.setAttribute("aria-hidden", "true")
        brandChevron.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>`

        brandFieldEl.appendChild(brandSelect)
        brandFieldEl.appendChild(brandChevron)
        stepEl.appendChild(brandFieldEl)

        const updateSelections = () => {
          this.selections[step.id] = {
            brand: brandSelect.value,
            motor: "",
            customBrand: ""
          }
          this.validateStep()
        }

        brandSelect.addEventListener("change", () => {
          updateSelections()
          if (brandSelect.value !== "") {
            this.nextStep()
          }
        })

        this.stepContainer.appendChild(stepEl)
      }

      renderOutcomeStep(step) {
        if (this.navsContainer) this.navsContainer.classList.add("hidden")

        const stepEl = document.createElement("div")
        stepEl.className = "intake-step active"
        stepEl.setAttribute("data-step-id", step.id)

        const outcomeClass = step.outcome_type === "success" ? "intake-result--success" : "intake-result--reject"
        const resultDiv = document.createElement("div")
        resultDiv.className = `intake-result ${outcomeClass}`

        // SVG Icon
        let svgHtml = ""
        if (step.outcome_type === "success") {
          svgHtml = `<svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>`
        } else {
          svgHtml = `<svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>`
        }
        resultDiv.innerHTML = svgHtml

        const h2 = document.createElement("h2")
        h2.textContent = step.title
        resultDiv.appendChild(h2)

        if (step.description) {
          let descHtml = step.description
          if (descHtml.includes("{XXX}")) {
            let brandName = ""
            const brandSelection = this.selections["check_brand"]
            if (brandSelection && typeof brandSelection === "object") {
              if (brandSelection.brand && brandSelection.brand.toLowerCase() !== "other") {
                brandName = brandSelection.brand
              }
            }
            if (!brandName || brandName.toLowerCase() === "other") {
              const motorSelection = this.selections["check_motor"]
              const motorStep = this.config.steps["check_motor"]
              if (motorSelection !== undefined && motorStep && motorStep.options) {
                const opt = motorStep.options[motorSelection]
                if (opt && opt.label) {
                  brandName = opt.label
                }
              }
            }
            if (brandName) {
              brandName = brandName.charAt(0).toUpperCase() + brandName.slice(1)
            } else {
              brandName = "your"
            }
            descHtml = descHtml.replace(/{XXX}/g, brandName)
          }

          const desc = document.createElement("div")
          desc.className = "text-body"
          desc.innerHTML = descHtml
          resultDiv.appendChild(desc)
        }

        // Add back button for outcome (in case they made a mistake)
        const buttonsDiv = document.createElement("div")
        buttonsDiv.className = "intake-outcome-buttons"

        const createActionLink = (buttonText, buttonUrl, isCalendly = false) => {
          if (!buttonText || !buttonUrl) return null

          const actionLink = document.createElement("a")
          actionLink.className = "button button--primary"
          actionLink.innerHTML = `<span>${buttonText}</span>`

          let finalUrl = buttonUrl
          if (isCalendly) {
            finalUrl = this.buildPrefilledCalendlyUrl(buttonUrl)
            actionLink.setAttribute("data-calendly-url", finalUrl)
            actionLink.href = "#"
          } else {
            actionLink.href = finalUrl
            if (finalUrl.startsWith("http://") || finalUrl.startsWith("https://")) {
              actionLink.target = "_blank"
              actionLink.rel = "noopener"
              actionLink.classList.add("button--external")
              actionLink.insertAdjacentHTML(
                "beforeend",
                `<span class="intake-external-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M14 3h7v7h-2V6.41l-9.29 9.3-1.42-1.42 9.3-9.29H14V3z"/><path d="M5 5h6v2H7v10h10v-4h2v6H5V5z"/></svg></span>`
              )
            }
          }
          if (!finalUrl.startsWith("http://") && !finalUrl.startsWith("https://")) {
            actionLink.insertAdjacentHTML(
              "beforeend",
              `<span class="button-icon-right" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="icon-arrow"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg></span>`
            )
          }

          return actionLink
        }

        const backBtn = document.createElement("button")
        backBtn.type = "button"
        backBtn.className = "button button--secondary"
        backBtn.innerHTML = `
          <span class="button-icon-left" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon-chevron"><polyline points="15 18 9 12 15 6"></polyline></svg>
          </span>
          <span>Back / Reset</span>
        `
        backBtn.addEventListener("click", () => this.prevStep())
        buttonsDiv.appendChild(backBtn)

        // Inline Calendly Embed or Action Button
        if (step.embed_calendly && step.button_url) {
          const embedContainer = document.createElement("div")
          embedContainer.className = "calendly-inline-widget intake-calendly-embed"

          const finalUrl = this.buildPrefilledCalendlyUrl(step.button_url)
          resultDiv.appendChild(embedContainer)
          this.initInlineCalendly(embedContainer, finalUrl)
        } else {
          const primaryActionLink = createActionLink(step.button_text, step.button_url, step.is_calendly)
          if (primaryActionLink) {
            buttonsDiv.appendChild(primaryActionLink)
          }

          const secondaryActionLink = createActionLink(step.secondary_button_text, step.secondary_button_url)
          if (secondaryActionLink) {
            buttonsDiv.appendChild(secondaryActionLink)
          }
        }
        resultDiv.appendChild(buttonsDiv)
        stepEl.appendChild(resultDiv)
        this.stepContainer.appendChild(stepEl)
      }

      getPrefillAnswer(step, selectionVal) {
        if (step.type === "brand_check") {
          if (selectionVal && typeof selectionVal === "object") {
            const brand = selectionVal.brand === "other" ? (selectionVal.customBrand || "").trim() : selectionVal.brand
            const motor = selectionVal.motor
            const brandFormatted = brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : ""
            const motorFormatted = motor ? motor.charAt(0).toUpperCase() + motor.slice(1) : ""
            if (brandFormatted) return `${brandFormatted}: [MODEL]`
            if (motorFormatted) return `Motor: ${motorFormatted}`
            return ""
          }
          return selectionVal ? `${selectionVal.charAt(0).toUpperCase() + selectionVal.slice(1)}: [MODEL]` : ""
        }

        if (step.type !== "question") return ""

        const chosenOption = step.options[selectionVal]
        if (!chosenOption) return ""

        // Calendly matches dropdown answers by exact option text.
        if (step.id === "bike_issue_type") {
          return chosenOption.label === "Electrical" ? "Ebike Electrical Diagnostic" : ""
        }

        if (step.id === "mechanical_tuneup_check") {
          return chosenOption.label === "Full Tune Up" ? "Tune Up" : ""
        }

        return chosenOption.label
      }

      buildCalendlyPrefill() {
        const customerName = this.dataset.customerName || window.ShopifyCustomerName || ""
        const customerEmail = this.dataset.customerEmail || window.ShopifyCustomerEmail || ""
        const prefill = {}
        const customAnswers = {}

        if (customerName) {
          prefill.name = customerName
        }
        if (customerEmail) {
          prefill.email = customerEmail
        }

        // Map responses to custom questions based on prefill_param settings.
        for (const stepId of Object.keys(this.selections)) {
          const step = this.config.steps[stepId]
          if (!step || !step.prefill_param || step.prefill_param === "none") continue

          const answerText = this.getPrefillAnswer(step, this.selections[stepId])
          if (answerText) {
            customAnswers[step.prefill_param] = answerText
          }
        }

        if (Object.keys(customAnswers).length) {
          prefill.customAnswers = customAnswers
        }

        return prefill
      }

      buildPrefilledCalendlyUrl(baseUrl) {
        const prefill = this.buildCalendlyPrefill()
        const calendlyUrl = new URL(baseUrl, window.location.origin)

        if (prefill.name) calendlyUrl.searchParams.set("name", prefill.name)
        if (prefill.email) calendlyUrl.searchParams.set("email", prefill.email)
        for (const [key, value] of Object.entries(prefill.customAnswers || {})) {
          calendlyUrl.searchParams.set(key, value)
        }

        const search = calendlyUrl.search.replace(/\+/g, "%20")
        return calendlyUrl.origin + calendlyUrl.pathname + search + calendlyUrl.hash
      }

      buildCalendlyWidgetOptions(container, url) {
        const calendlyUrl = new URL(url, window.location.origin)
        const prefill = {}
        const customAnswers = {}
        const name = calendlyUrl.searchParams.get("name")
        if (name) {
          prefill.name = name
          calendlyUrl.searchParams.delete("name")
        }

        const email = calendlyUrl.searchParams.get("email")
        if (email) {
          prefill.email = email
          calendlyUrl.searchParams.delete("email")
        }

        for (const key of Array.from(calendlyUrl.searchParams.keys())) {
          if (!/^a\d+$/.test(key)) continue

          const value = calendlyUrl.searchParams.get(key)
          calendlyUrl.searchParams.delete(key)
          if (value) {
            customAnswers[key] = value
          }
        }

        if (Object.keys(customAnswers).length) {
          prefill.customAnswers = customAnswers
        }

        if (!calendlyUrl.searchParams.has("hide_gdpr_banner")) {
          calendlyUrl.searchParams.set("hide_gdpr_banner", "1")
        }

        const search = calendlyUrl.search.replace(/\+/g, "%20")
        const widgetOptions = {
          url: calendlyUrl.origin + calendlyUrl.pathname + search + calendlyUrl.hash,
          parentElement: container
        }
        if (prefill.name || prefill.email || prefill.customAnswers) {
          widgetOptions.prefill = prefill
        }
        return widgetOptions
      }

      initInlineCalendly(container, url) {
        const widgetOptions = this.buildCalendlyWidgetOptions(container, url)

        if (window.Calendly) {
          window.Calendly.initInlineWidget(widgetOptions)
          return
        }

        // If not loaded, inject stylesheet & script
        if (!document.querySelector('link[href="https://assets.calendly.com/assets/external/widget.css"]')) {
          const link = document.createElement("link")
          link.rel = "stylesheet"
          link.href = "https://assets.calendly.com/assets/external/widget.css"
          document.head.appendChild(link)
        }

        const scriptSrc = "https://assets.calendly.com/assets/external/widget.js"
        let script = document.querySelector(`script[src="${scriptSrc}"]`)
        if (!script) {
          script = document.createElement("script")
          script.src = scriptSrc
          script.async = true
          document.head.appendChild(script)
        }

        script.addEventListener("load", () => {
          if (window.Calendly) {
            window.Calendly.initInlineWidget(widgetOptions)
          }
        })
      }

      validateStep() {
        const step = this.config.steps[this.currentStepId]
        let isValid = false

        if (!step) return

        if (step.type === "question") {
          isValid = this.selections[this.currentStepId] !== undefined
        } else if (step.type === "brand_check") {
          const val = this.selections[this.currentStepId]
          if (val && typeof val === "object") {
            isValid = val.brand !== ""
          } else {
            isValid = val !== undefined && val !== ""
          }
        } else if (step.type === "outcome") {
          isValid = true
        }

        if (this.nextBtn) {
          this.nextBtn.disabled = !isValid
        }
        this.prevBtn.disabled = this.showLanding
      }

      nextStep() {
        const step = this.config.steps[this.currentStepId]
        let nextStepId = null

        if (!step) return

        if (step.type === "question") {
          const selectedIdx = this.selections[this.currentStepId]
          const option = step.options[selectedIdx]
          if (option) {
            nextStepId = option.next
          }
        } else if (step.type === "brand_check") {
          const val = this.selections[this.currentStepId]
          let isSupported = false

          if (val && typeof val === "object") {
            if (val.brand && val.brand !== "other") {
              isSupported = true
            }
          } else if (val) {
            isSupported = val.toLowerCase() !== "other"
          }

          if (isSupported) {
            nextStepId = step.next_supported
          } else {
            nextStepId = step.next_unsupported
          }
        }

        if (nextStepId) {
          this.history.push(this.currentStepId)
          this.currentStepId = nextStepId
          this.renderStep(this.currentStepId)
        }
      }

      prevStep() {
        if (this.history.length === 0) {
          this.currentStepId = this.config.start_step_id || "start"
          this.selections = {}
          this.renderLandingStep()
          return
        }
        this.currentStepId = this.history.pop()
        this.renderStep(this.currentStepId)
      }

      updateBreadcrumbs() {
        if (!this.breadcrumbsContainer) return
        this.breadcrumbsContainer.innerHTML = ""

        if (this.showLanding) {
          this.breadcrumbsContainer.classList.add("hidden")
          return
        }

        this.breadcrumbsContainer.classList.remove("hidden")

        // First breadcrumb item: Service Intake Form (links back to landing)
        const startLink = document.createElement("span")
        startLink.className = "intake-breadcrumb-item"
        startLink.textContent = "Service Intake Form"
        startLink.addEventListener("click", () => {
          this.history = []
          this.selections = {}
          this.currentStepId = this.config.start_step_id || "start"
          this.renderLandingStep()
        })
        this.breadcrumbsContainer.appendChild(startLink)

        for (const [index, stepId] of this.history.entries()) {
          const step = this.config.steps[stepId]
          if (!step) continue

          const selectionVal = this.selections[stepId]
          let chosenLabel = ""

          if (step.type === "question") {
            const chosenOption = step.options[selectionVal]
            if (chosenOption) {
              chosenLabel = chosenOption.label
              if (index === 0) {
                chosenLabel = "Vehicle: " + chosenLabel
              } else if (stepId === "check_motor") {
                chosenLabel = "Motor: " + chosenLabel
              } else {
                chosenLabel = "Issue: " + chosenLabel
              }
            }
          } else if (step.type === "brand_check") {
            if (selectionVal) {
              if (typeof selectionVal === "object") {
                const b = selectionVal.brand
                const brandFormatted = b ? b.charAt(0).toUpperCase() + b.slice(1) : ""
                if (brandFormatted) {
                  chosenLabel = "Brand: " + brandFormatted
                }
              } else {
                chosenLabel = "Brand: " + selectionVal.charAt(0).toUpperCase() + selectionVal.slice(1)
              }
            }
          }

          if (!chosenLabel) continue

          // Separator
          const separator = document.createElement("span")
          separator.className = "intake-breadcrumb-separator"
          separator.textContent = ">"
          this.breadcrumbsContainer.appendChild(separator)

          // Link
          let emoji = ""
          const lowerLabel = chosenLabel.toLowerCase()
          if (lowerLabel.includes("e-bike") || lowerLabel.includes("ebike")) {
            emoji = "🔋 "
          } else if (lowerLabel.includes("regular bike") || lowerLabel.includes("bike")) {
            emoji = "🚲 "
          } else if (lowerLabel.includes("mechanical")) {
            emoji = "⚙️ "
          } else if (lowerLabel.includes("scooter")) {
            emoji = "🛴 "
          } else if (lowerLabel.includes("electrical")) {
            emoji = "⚡ "
          } else if (lowerLabel.includes("e-moto") || lowerLabel.includes("emoto")) {
            emoji = "🏍️ "
          }

          const link = document.createElement("span")
          link.className = "intake-breadcrumb-item"
          link.textContent = emoji + chosenLabel
          link.addEventListener("click", () => {
            this.history = this.history.slice(0, index)
            this.currentStepId = stepId
            this.renderStep(stepId)
          })
          this.breadcrumbsContainer.appendChild(link)
        }
      }

      pruneFutureSelections() {
        const activeSteps = new Set([...this.history, this.currentStepId])
        for (const stepId of Object.keys(this.selections)) {
          if (!activeSteps.has(stepId)) {
            delete this.selections[stepId]
          }
        }
      }
    }
  )
}
